import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { Spinner, StatusBadge } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Archive, RotateCcw, Trash2, Plus, Download } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function BackupsPanel({ cloudId }) {
    const { data: backups, mutate, isLoading } = useSWR(
        `/api/admin/clouds/${cloudId}/backups`,
        fetcher,
        { refreshInterval: 3000 },
    );
    const [busy, setBusy] = useState(false);
    const [kind, setKind] = useState("full");
    const [err, setErr] = useState(null);

    const trigger = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/backups`, {
                kind,
                retention_days: 30,
            });
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    const restore = async (id) => {
        if (!window.confirm(
            "Restore this backup?\n\n" +
            "• The cloud briefly enters maintenance mode.\n" +
            "• Database AND the NAS directory (config/custom_apps/themes) " +
            "are reset to the state of the backup.\n" +
            "• Current data will be overwritten.\n\n" +
            "Continue?")) return;
        try {
            await api.post(`/api/admin/clouds/${cloudId}/backups/${id}/restore`);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this backup permanently?")) return;
        await api.delete(`/api/admin/clouds/${cloudId}/backups/${id}`);
        await mutate();
    };

    const compLabel = (b) => {
        const c = b?.manifest?.components;
        if (!c?.length) return null;
        return c.map((x) => x === "nas" ? "NAS" : x.replace("db:", "DB:")).join(" · ");
    };

    // Cookie-auth (withCredentials) → a plain link carries the session cookie,
    // and Content-Disposition: attachment makes the browser download the file.
    const dlUrl = (b, component, label) => {
        const base = `${API_BASE}/api/admin/clouds/${cloudId}/backups/${b.id}/download`;
        return component === "db"
            ? `${base}?component=db&label=${label}`
            : `${base}?component=nas`;
    };

    const downloadItems = (b) => {
        const c = b?.manifest?.components || [];
        return c.map((x) => {
            if (x === "nas") return { key: "nas", label: "NAS", component: "nas" };
            const lbl = x.replace("db:", "");
            return { key: x, label: `DB:${lbl}`, component: "db", dbLabel: lbl };
        });
    };

    return (
        <div className="cm-card p-5" data-testid="backups-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Archive size={14} className="text-zinc-400" />
                    <div className="cm-h3">Backups</div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        className="cm-input text-xs max-w-[140px]"
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        data-testid="backups-kind-select"
                    >
                        <option value="full">Full (DB + NAS)</option>
                        <option value="db">Database only</option>
                        <option value="files">NAS files only</option>
                    </select>
                    <button
                        onClick={trigger}
                        disabled={busy}
                        className="cm-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40"
                        data-testid="backups-trigger-button"
                    >
                        <Plus size={12} /> New backup
                    </button>
                </div>
            </div>
            <div className="text-xs text-zinc-500 mb-2">
                DB dump (cloud-owned user) + NAS archive, persisted on the CM node.
                Restore is manual. The last 3 backups are kept — pre-import backups
                are kept permanently.
            </div>
            {err && <div className="text-xs text-cm-danger mb-2">{err}</div>}
            {isLoading ? (
                <Spinner />
            ) : !backups?.length ? (
                <div className="text-xs text-zinc-500 py-3 text-center bg-zinc-950 border border-zinc-800">
                    No backups yet
                </div>
            ) : (
                <div className="space-y-2">
                    {backups.slice(0, 10).map((b) => (
                        <div
                            key={b.id}
                            className="bg-zinc-950 border border-zinc-800 p-2 text-xs flex justify-between items-center"
                            data-testid={`backup-row-${b.id}`}
                        >
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-zinc-100">{b.kind}</span>
                                    <StatusBadge status={b.status === "completed" ? "active" : b.status === "creating" ? "provisioning" : b.status} />
                                    {b.protected && (
                                        <span
                                            className="font-mono text-[11px] text-amber-300 border border-amber-700/40 px-1"
                                            title="Retained permanently — excluded from rotation"
                                            data-testid={`backup-protected-${b.id}`}
                                        >
                                            protected
                                        </span>
                                    )}
                                    {compLabel(b) && (
                                        <span className="font-mono text-[11px] text-zinc-400">{compLabel(b)}</span>
                                    )}
                                </div>
                                <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                                    {b.size_gb ? `${b.size_gb} GB` : "—"} · {fmtRel(b.created_at)}
                                    {b.trigger === "pre-update" ? " · Auto before update" : ""}
                                    {(b.trigger === "pre-import" || b.pre_import) ? " · Auto before import" : ""}
                                </div>
                                {b.note && <div className="text-xs text-zinc-400 mt-0.5">{b.note}</div>}
                                {b.status === "failed" && b.error && (
                                    <div className="text-xs text-cm-danger mt-0.5">{b.error}</div>
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                {b.status === "completed" && downloadItems(b).map((d) => (
                                    <a
                                        key={d.key}
                                        href={dlUrl(b, d.component, d.dbLabel)}
                                        download
                                        className="cm-btn-ghost text-xs flex items-center gap-1 text-sky-300"
                                        data-testid={`backup-download-${d.component}${d.dbLabel ? "-" + d.dbLabel : ""}-${b.id}`}
                                        title={`Download backup component: ${d.label}`}
                                    >
                                        <Download size={11} /> {d.label}
                                    </a>
                                ))}
                                {b.status === "completed" && (
                                    <button
                                        onClick={() => restore(b.id)}
                                        className="cm-btn-ghost text-xs flex items-center gap-1"
                                        data-testid={`backup-restore-${b.id}`}
                                    >
                                        <RotateCcw size={11} /> Restore
                                    </button>
                                )}
                                <button
                                    onClick={() => remove(b.id)}
                                    className="cm-btn-ghost text-xs flex items-center gap-1 text-cm-danger"
                                    data-testid={`backup-delete-${b.id}`}
                                >
                                    <Trash2 size={11} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
